import api from './api';

/**
 * Access & emergency API service — QR access, panic alerts, and travel notices.
 * Routes are under /api/qr-access/*, /api/panic/*, /api/travel/*
 */
export const accessApi = {
  qrAccess: {
    myCodes: () => api.get('/qr-access/my-codes'),
    // GET /qr-access/my-pass (ADDENDUM 2026-09-14b Phase 1): per-USER rotating
    // pass secret for the VQR2 "Mi pase" (one pass = the person). Returns
    // { user_id, qr_secret, sample_payload, manual_digits_sample }.
    myPass: () => api.get('/qr-access/my-pass'),
    generate: (data: {
      visitorName: string;
      phone?: string;
      validUntil: string;
      condoId?: string;
      [key: string]: any;
    }) => api.post('/qr-access/generate', data),
    revoke: (id: string) => api.delete(`/qr-access/codes/${id}`),
    // Editar/Extender vigencia (2026-09-11): partial validity update. Body
    // { arrivalAt?, windowHours?, extendHours?, validFrom?, validUntil? } ->
    // 200 { success, data: { id, arrival_at, window_hours, valid_from_date, valid_until_date, status } }.
    patchValidity: (id: string, data: {
      arrivalAt?: string;
      windowHours?: number;
      extendHours?: number;
      validFrom?: string | null;
      validUntil?: string | null;
      [key: string]: any;
    }) => api.patch(`/qr-access/codes/${id}/validity`, data),
    // Phase 2 link invites (2026-09-12): owner-only invite-link for a guest
    // code. POST /qr-access/codes/:id/invite-link -> { success, data:{ url } }
    // (full https link; the LINK is the credential). 404 until the backend
    // deploys — the FE action degrades to the QR share on 404.
    inviteLink: (id: string) => api.post(`/qr-access/codes/${id}/invite-link`),
  },
  panic: {
    // Lane D panic rework (kimi plan 2026-10-09): trigger accepts the
    // panic-kind discriminator and unit scoping. unitId preferred (active
    // role row unit_id); condoId used as fallback when there is no unit.
    // kind: 'panic' | 'stuck_elevator'. 201 -> { alert, notified }; duplicate
    // within 2 min -> 409 { code: 'PANIC_DUPLICATE' }.
    trigger: (data: {
      alertType: string;
      kind?: 'panic' | 'stuck_elevator';
      unitId?: string;
      notes?: string;
      condoId?: string;
      [key: string]: any;
    }) =>
      api.post('/panic/trigger', data),
    active: () => api.get('/panic/active'),
    respond: (id: string, data?: { notes?: string }) =>
      api.put(`/panic/${id}/respond`, data || {}),
    resolve: (id: string, data?: { resolution?: string }) =>
      api.put(`/panic/${id}/resolve`, data || {}),
  },
  travel: {
    list: () => api.get('/travel/notices'),
    create: (data: {
      startDate: string;
      endDate: string;
      emergencyContact: string;
      notes?: string;
      condoId?: string;
      [key: string]: any;
    }) => api.post('/travel/notices', data),
    update: (id: string, data: Partial<{
      startDate: string;
      endDate: string;
      emergencyContact: string;
      notes: string;
    }>) => api.put(`/travel/notices/${id}`, data),
    cancel: (id: string) => api.delete(`/travel/notices/${id}`),
  },
};

export default accessApi;

// ============================================================
// Walk-in "Sin QR" (roadmap item E, approved 2026-09-16). A guard registers a
// visitor without a QR code: POST /api/security/walk-ins creates an
// access_code with origin='walk_in' in status 'pending_approval'; the host
// approves/denies from MyQR via POST /api/walk-ins/:id/approve|deny. Codes
// auto-cancel after 15 min without a decision (backend stamps a
// WALK_IN_TIMEOUT cancel reason). Frontend codes defensively against this
// contract — every field is aliased and every render degrades gracefully.
// ============================================================
export interface WalkIn {
  id: string;
  unit_id?: string; unitId?: string;
  condo_id?: string; condoId?: string;
  visitor_name?: string; visitorName?: string;
  phone?: string;
  /** Walk-in enhancement (Jaime 2026-09-16): optional ID document number. */
  visitor_id_number?: string; visitorIdNumber?: string;
  /** Walk-in enhancement (Jaime 2026-09-16): ID photo as data URL, same
   *  shape the /access/id-capture flow uses (compressed JPEG data URL). */
  visitor_photo?: string; visitorPhoto?: string;
  plate?: string; vehicle_plate?: string;
  reason?: string;
  duration_hours?: number; durationHours?: number;
  access_code?: string; code?: string;
  status: 'pending_approval' | 'approved' | 'denied' | 'expired' | 'cancelled' | string;
  cancel_reason?: string; reason_code?: string;
  origin?: string;
  unit_label?: string; unitLabel?: string;
  created_at?: string; createdAt?: string;
}

export type WalkInEffectiveStatus = 'pending_approval' | 'approved' | 'denied' | 'expired' | 'timeout_cancelled';

// Badge mapping (item 4): walk-in states extend — never replace — the existing
// 4-state invite badge system (Permiso Activo / Vigente / Expirado / Cancelado),
// which remains intact for QR invites.
export function walkInEffectiveStatus(w: Pick<WalkIn, 'status' | 'cancel_reason' | 'reason_code'>): WalkInEffectiveStatus {
  const raw = (w.status || '').toLowerCase();
  const cancelReason = w.cancel_reason || w.reason_code || '';
  if (raw === 'cancelled' || /WALK_IN_TIMEOUT/i.test(cancelReason)) return 'timeout_cancelled';
  if (raw === 'pending_approval' || raw === 'pending') return 'pending_approval';
  // FIX 2026-09-16 (B1): backend stamps status 'active' (not 'approved') on
  // host approval, so approved walk-ins rendered as "Expirado". Map both.
  if (raw === 'approved' || raw === 'active') return 'approved';
  if (raw === 'denied') return 'denied';
  return 'expired';
}

export const WALK_IN_STATUS_BADGE: Record<WalkInEffectiveStatus, { label: string; cls: string }> = {
  pending_approval: { label: 'Pendiente de aprobación', cls: 'bg-amber-500/25 text-amber-300' },
  approved: { label: 'Aprobado', cls: 'bg-emerald-500/25 text-emerald-300' },
  denied: { label: 'Rechazado', cls: 'bg-red-500/25 text-red-300' },
  expired: { label: 'Expirado', cls: 'bg-gray-500/25 text-gray-300' },
  timeout_cancelled: { label: 'Cancelado (sin aprobación)', cls: 'bg-red-500/15 text-red-300/80' },
};

export const walkInApi = {
  // POST /api/security/walk-ins {unitId, visitorName, phone?, plate?, reason?,
  // durationHours (≤24)} → { success, data: { walk_in: { access_code, status:
  // 'pending_approval', ... } } }. Defensive: data.walk_in ?? data ?? bare row.
  create: (data: {
    unitId: string;
    visitorName: string;
    /** Walk-in enhancement (Jaime 2026-09-16): optional ID document number. */
    visitor_id_number?: string;
    /** Walk-in enhancement (Jaime 2026-09-16): compressed JPEG data URL, same
     *  shape POST /access/id-capture accepts as `image`. */
    visitor_photo?: string;
    phone?: string;
    plate?: string;
    reason?: string;
    durationHours: number;
    condoId?: string;
    [key: string]: any;
  }) => api.post('/security/walk-ins', data),
  // GET /api/security/walk-ins?condoId=&status= → { success, data: { walk_ins |
  // items | list: [...] } } (bare array fallback).
  list: (condoId: string, status?: string) =>
    api.get('/security/walk-ins', { params: { condoId, ...(status ? { status } : {}) } }),
  // GET /api/walk-ins/mine?status= (FIX 2026-09-16, B2): host-scoped walk-ins
  // for the current user's own units. GET /security/walk-ins is guard-scoped
  // (residents get 403) and its rows lack unit_id, so it never worked for the
  // host approval card. Locked contract: rows { id, code, status
  // (pending_approval|active|denied|cancelled), visitor_name, phone, plate,
  // reason, unit_id, unit_label, created_at, valid-window fields }.
  mine: (status?: string) =>
    api.get('/walk-ins/mine', { params: status ? { status } : {} }),
  // Unit-member decision endpoints (backend enforces membership).
  approve: (id: string) => api.post(`/walk-ins/${id}/approve`),
  deny: (id: string) => api.post(`/walk-ins/${id}/deny`),
  // UX-3 (2026-09-16): guard "Llamar residente" contacts for a unit.
  // GET /api/security/walk-ins/contacts?unitId= → [{ name, phone }]
  // (resident-class members only; phone may be null). Backend deploys in
  // parallel — callers must degrade gracefully on 404.
  contacts: (unitId: string) =>
    api.get('/security/walk-ins/contacts', { params: { unitId } }),
};

// Defensive list normalization shared by the guard list (VerifyQR) and the
// host approval card (MyQR).
export function normalizeWalkIn(raw: unknown, i = 0): WalkIn {
  const r = (raw ?? {}) as Record<string, unknown>;
  const pick = (...keys: string[]): string | undefined => {
    for (const k of keys) {
      const v = r[k];
      if (typeof v === 'string' && v.trim()) return v.trim();
      if (typeof v === 'number') return String(v);
    }
    return undefined;
  };
  return {
    id: pick('id', 'walk_in_id') ?? `walkin-${i}`,
    unit_id: pick('unit_id', 'unitId'),
    condo_id: pick('condo_id', 'condoId'),
    visitor_name: pick('visitor_name', 'visitorName', 'name'),
    phone: pick('phone', 'visitor_phone'),
    visitor_id_number: pick('visitor_id_number', 'visitorIdNumber', 'id_number', 'idNumber'),
    visitor_photo: pick('visitor_photo', 'visitorPhoto', 'photo', 'photo_url'),
    plate: pick('plate', 'vehicle_plate', 'vehiclePlate'),
    reason: pick('reason', 'notes'),
    duration_hours: typeof r.duration_hours === 'number' ? r.duration_hours : typeof r.durationHours === 'number' ? r.durationHours : undefined,
    access_code: pick('access_code', 'code'),
    status: pick('status') ?? 'pending_approval',
    cancel_reason: pick('cancel_reason', 'reason_code'),
    origin: pick('origin'),
    unit_label: pick('unit_label', 'unitLabel', 'unit'),
    created_at: pick('created_at', 'createdAt'),
  };
}

export function walkInListFromResponse(resData: any): WalkIn[] {
  const payload = resData?.data ?? resData;
  const list = payload?.walk_ins ?? payload?.walkIns ?? payload?.items ?? payload?.list
    ?? (Array.isArray(payload) ? payload : []);
  return (Array.isArray(list) ? list : []).map(normalizeWalkIn);
}

